import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Play, ExternalLink, Video } from "lucide-react";

interface YouTubePlayerProps {
  videoUrl: string;
  title?: string;
  className?: string;
  autoplay?: boolean;
}

// Extract YouTube video ID from various URL formats
const extractVideoId = (url: string): string | null => {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
  ];
  
  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }
  return null;
};

// Get YouTube thumbnail URL
const getThumbnailUrl = (videoId: string, quality: 'default' | 'medium' | 'high' | 'maxres' = 'high'): string => {
  return `https://img.youtube.com/vi/${videoId}/${quality === 'high' ? 'hqdefault' : quality === 'maxres' ? 'maxresdefault' : quality === 'medium' ? 'mqdefault' : 'default'}.jpg`;
};

export const YouTubePlayer: React.FC<YouTubePlayerProps> = ({ 
  videoUrl, 
  title,
  className = "",
  autoplay = false 
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasError, setHasError] = useState(false);
  
  const videoId = extractVideoId(videoUrl);
  
  if (!videoId || hasError) {
    return (
      <Card className={`bg-muted/50 ${className}`}>
        <CardContent className="flex items-center justify-center p-8">
          <div className="text-center">
            <Video className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground mb-4">
              {hasError ? "Failed to load video" : "Invalid YouTube URL"}
            </p>
            <Button asChild variant="outline" size="sm">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                Watch on YouTube
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const thumbnailUrl = getThumbnailUrl(videoId);
  const embedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=${autoplay ? 1 : 0}&rel=0&modestbranding=1`;
  
  if (!isPlaying) {
    return (
      <Card className={`overflow-hidden hover:shadow-glow-primary transition-all duration-300 ${className}`}>
        <div className="relative group cursor-pointer" onClick={() => setIsPlaying(true)}>
          <div className="aspect-video relative overflow-hidden">
            <img
              src={thumbnailUrl}
              alt={title || "YouTube video thumbnail"}
              className="w-full h-full object-cover transition-transform group-hover:scale-105"
              onError={() => setHasError(true)}
            />
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/30 transition-colors" />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="bg-red-600 hover:bg-red-700 text-white rounded-full p-4 group-hover:scale-110 transition-transform shadow-lg">
                <Play className="h-8 w-8 ml-1" fill="currentColor" />
              </div>
            </div>
          </div>
          
          {title && (
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-3">
                <CardTitle className="text-lg line-clamp-2 group-hover:text-primary transition-colors">
                  {title}
                </CardTitle>
                <Badge variant="secondary" className="flex-shrink-0">
                  <Video className="h-3 w-3 mr-1" />
                  Video
                </Badge>
              </div>
              <CardDescription className="flex items-center gap-2">
                <span>YouTube • Click to play</span>
              </CardDescription>
            </CardHeader>
          )}
        </div>
      </Card>
    );
  }
  
  return (
    <Card className={`overflow-hidden ${className}`}>
      <div className="aspect-video">
        <iframe
          src={embedUrl}
          title={title || "YouTube video"}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          onError={() => setHasError(true)}
        />
      </div>
      
      {title && (
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-3">
            <CardTitle className="text-lg line-clamp-2">
              {title}
            </CardTitle>
            <Button asChild variant="outline" size="sm">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                YouTube
              </a>
            </Button>
          </div>
        </CardHeader>
      )}
    </Card>
  );
};